import React, { useState, useCallback } from 'react';
import { X, Calendar, Copy, Bookmark, Share2, Check, Clipboard } from 'lucide-react';
import { useAppData } from '../context/AppContext';
import styles from './AppointmentSummaryModal.module.css';

const PERIOD_OPTIONS = [
  { label: '1 Week', days: 7 },
  { label: '2 Weeks', days: 14 },
  { label: '4 Weeks', days: 28 },
];

function toDateStr(d: Date): string {
  return d.toISOString().split('T')[0];
}

function formatDateFriendly(d: Date): string {
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

function formatTime(d: Date): string {
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

function getPainLabel(level: number): string {
  if (level <= 2) return 'minimal';
  if (level <= 4) return 'mild';
  if (level <= 6) return 'moderate';
  if (level <= 8) return 'significant';
  return 'severe';
}

interface Appointment {
  id: string;
  title: string;
  date: Date;
  location?: string;
  notes?: string;
}

interface Props {
  appointment: Appointment;
  onClose: () => void;
}

export default function AppointmentSummaryModal({ appointment, onClose }: Props) {
  const { painLogs, exerciseCompletions, prescribedExercises, documents, addNote } = useAppData();
  const [selectedDays, setSelectedDays] = useState(14);
  const [copied, setCopied] = useState(false);
  const [saved, setSaved] = useState(false);

  const generateSummary = useCallback((): string => {
    const now = new Date();
    const fromDate = new Date(now);
    fromDate.setDate(fromDate.getDate() - selectedDays);
    const fromStr = toDateStr(fromDate);
    const todayStr = toDateStr(now);

    const SHORT_DATE = (d: Date) =>
      d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });

    const section = (title: string) => `\n${title}\n${'─'.repeat(title.length)}`;
    const lines: string[] = [];

    lines.push('Recovery Summary');
    lines.push('─'.repeat(32));
    lines.push(`${appointment.title}${appointment.location ? `  ·  ${appointment.location}` : ''}`);
    lines.push(`${SHORT_DATE(fromDate)}  to  ${SHORT_DATE(now)}  (${selectedDays} days)`);
    lines.push(`Prepared ${formatDateFriendly(now)} at ${formatTime(now)}`);

    // Pain
    const periodPainLogs = (painLogs as any[])
      .filter((p) => {
        const d = toDateStr(new Date(p.timestamp));
        return d >= fromStr && d <= todayStr;
      })
      .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

    lines.push(section('Pain'));

    if (periodPainLogs.length === 0) {
      lines.push('No pain entries logged in this period.');
    } else {
      const avg = periodPainLogs.reduce((s, p) => s + p.level, 0) / periodPainLogs.length;
      const max = Math.max(...periodPainLogs.map((p) => p.level));
      const min = Math.min(...periodPainLogs.map((p) => p.level));
      const uniqueDays = new Set(periodPainLogs.map((p) => toDateStr(new Date(p.timestamp)))).size;

      lines.push(`Average   ${avg.toFixed(1)} / 10   (${getPainLabel(avg)})`);
      lines.push(`Range     ${min} – ${max} / 10`);
      lines.push(`Logged    ${periodPainLogs.length} ${periodPainLogs.length === 1 ? 'entry' : 'entries'} across ${uniqueDays} ${uniqueDays === 1 ? 'day' : 'days'}`);

      const grouped: Record<string, typeof periodPainLogs> = {};
      periodPainLogs.forEach((p) => {
        const k = toDateStr(new Date(p.timestamp));
        if (!grouped[k]) grouped[k] = [];
        grouped[k].push(p);
      });

      lines.push('');
      Object.entries(grouped).forEach(([dateStr, entries]) => {
        const d = new Date(dateStr + 'T12:00:00');
        const dayAvg = entries.reduce((s, p) => s + p.level, 0) / entries.length;
        const locations = [...new Set(entries.map((p) => p.location).filter(Boolean))];
        lines.push(
          `${SHORT_DATE(d)}   ${dayAvg.toFixed(1)}/10   ${getPainLabel(dayAvg)}${locations.length > 0 ? `   (${locations.join(', ')})` : ''}`
        );
        entries.forEach((p) => {
          if (p.notes?.trim()) lines.push(`  "${p.notes.trim()}"`);
        });
      });
    }

    // Exercise
    lines.push(section('Exercise'));

    if ((prescribedExercises as any[]).length === 0) {
      lines.push('No prescribed exercises on file.');
    } else {
      const uniqueExDates = new Set(
        (exerciseCompletions as any[])
          .filter((c) => c.date >= fromStr && c.date <= todayStr)
          .map((c) => c.date)
      ).size;

      lines.push(`Active days   ${uniqueExDates} of ${selectedDays}`);
      lines.push('');

      (prescribedExercises as any[]).forEach((ex) => {
        const completions = (exerciseCompletions as any[])
          .filter((c) => c.exerciseId === ex.id && c.date >= fromStr && c.date <= todayStr)
          .sort((a, b) => a.date.localeCompare(b.date));

        const detail =
          ex.exerciseType === 'cardio'
            ? `${ex.duration} min`
            : `${ex.sets} × ${ex.reps}${ex.weight > 0 ? ` @ ${ex.weight}kg` : ''}`;

        const sessionWord = completions.length === 1 ? 'session' : 'sessions';
        lines.push(`${ex.name}`);
        lines.push(`  ${detail}   ·   ${completions.length} ${sessionWord}`);

        completions.forEach((c: any) => {
          const d = new Date(c.date + 'T12:00:00');
          const notePart = c.notes?.trim() ? `  — "${c.notes.trim()}"` : '';
          lines.push(`  ${SHORT_DATE(d)}${notePart}`);
        });
      });

      const allCompletions = (prescribedExercises as any[]).map((ex) => {
        const comps = (exerciseCompletions as any[]).filter(
          (c) => c.exerciseId === ex.id && c.date >= fromStr && c.date <= todayStr
        );
        const allNotes = comps.map((c: any) => c.notes).filter((n: any) => n?.trim()) as string[];
        return { ex, count: comps.length, notes: allNotes };
      }).filter((e) => e.count > 0);

      if (allCompletions.length > 0) {
        lines.push('');
        const summaryParts: string[] = [];
        const totalSessions = allCompletions.reduce((s, e) => s + e.count, 0);

        if (allCompletions.length === 1) {
          summaryParts.push(
            `Over this period, ${allCompletions[0].ex.name} was completed ${totalSessions} ${totalSessions === 1 ? 'time' : 'times'}.`
          );
        } else {
          const sorted = [...allCompletions].sort((a, b) => b.count - a.count);
          const most = sorted[0];
          const least = sorted[sorted.length - 1];
          summaryParts.push(
            `Over this period, ${totalSessions} exercise sessions were logged across ${allCompletions.length} exercises.`
          );
          if (most.count !== least.count) {
            summaryParts.push(
              `${most.ex.name} was the most consistently completed (${most.count} ${most.count === 1 ? 'session' : 'sessions'}), while ${least.ex.name} had fewer sessions (${least.count}).`
            );
          }
        }

        const allSessionNotes = allCompletions.flatMap((e) => e.notes);
        if (allSessionNotes.length > 0) {
          const noteSample = allSessionNotes.slice(-2);
          if (noteSample.length === 1) {
            summaryParts.push(`A key observation noted: "${noteSample[0]}".`);
          } else {
            summaryParts.push(
              `Key observations included: "${noteSample[0]}" and "${noteSample[noteSample.length - 1]}".`
            );
          }
        } else {
          summaryParts.push('No session notes were recorded during this period.');
        }

        lines.push(summaryParts.join(' '));
      }
    }

    // Documents
    if ((documents as any[]).length > 0) {
      const periodDocs = (documents as any[]).filter((d) => {
        const dStr = toDateStr(new Date(d.timestamp));
        return dStr >= fromStr && dStr <= todayStr;
      });
      if (periodDocs.length > 0) {
        lines.push(section('Documents uploaded'));
        periodDocs.forEach((d: any) => {
          lines.push(`${d.name}   (${d.type.replace('_', ' ')})`);
          if (d.notes?.trim()) lines.push(`  "${d.notes.trim()}"`);
        });
      }
    }

    // Appointment notes
    if (appointment.notes?.trim()) {
      lines.push(section('Notes for this appointment'));
      lines.push(appointment.notes.trim());
    }

    lines.push('\n' + '─'.repeat(32));
    lines.push('Generated by beryl · Your Recovery Buddy');

    return lines.join('\n');
  }, [selectedDays, painLogs, exerciseCompletions, prescribedExercises, documents, appointment]);

  const summary = generateSummary();

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(summary);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      /* fallback silent */
    }
  };

  const handleSave = async () => {
    const now = new Date();
    const title = `${appointment.title} Summary — ${now.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}`;
    await addNote({ title, content: summary, category: 'Appointment' });
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: `Recovery Summary — ${appointment.title}`, text: summary });
      } catch { /* dismissed */ }
    } else {
      await handleCopy();
    }
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <button className={styles.closeBtn} onClick={onClose}>
            <X size={20} />
          </button>
          <h2 className={styles.title}>Appointment Summary</h2>
          <div style={{ width: 36 }} />
        </div>

        <div className={styles.banner}>
          <Calendar size={14} />
          <span className={styles.bannerText}>
            {appointment.title}{appointment.location ? ` · ${appointment.location}` : ''}
          </span>
        </div>

        <div className={styles.periodRow}>
          <span className={styles.periodLabel}>Summarise the last:</span>
          <div className={styles.periodBtns}>
            {PERIOD_OPTIONS.map((opt) => (
              <button
                key={opt.days}
                className={`${styles.periodBtn} ${selectedDays === opt.days ? styles.periodBtnActive : ''}`}
                onClick={() => setSelectedDays(opt.days)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className={styles.summaryScroll}>
          <pre className={styles.summaryText}>{summary}</pre>
        </div>

        <div className={styles.footer}>
          <button
            className={`${styles.actionBtn} ${copied ? styles.actionBtnSuccess : ''}`}
            onClick={handleCopy}
          >
            {copied ? <Check size={16} /> : <Copy size={16} />}
            {copied ? 'Copied!' : 'Copy'}
          </button>
          <button
            className={`${styles.actionBtn} ${saved ? styles.actionBtnSuccess : ''}`}
            onClick={handleSave}
          >
            {saved ? <Check size={16} /> : <Bookmark size={16} />}
            {saved ? 'Saved!' : 'Save'}
          </button>
          <button className={`${styles.actionBtn} ${styles.shareBtn}`} onClick={handleShare}>
            <Share2 size={16} />
            Share
          </button>
        </div>
      </div>
    </div>
  );
}
